import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import TestimonialCarousel from "@/components/TestimonialCarousel";
import SEOHead from "@/components/SEOHead";

const HERO_IMAGE_BASE = "https://res.cloudinary.com/dex9lnark/image/upload/f_auto,q_auto";
const CTA_IMAGE_BASE = "https://res.cloudinary.com/dex9lnark/image/upload/f_auto,q_auto,c_limit";

const HERO_IMAGE_SRC = `${HERO_IMAGE_BASE},w_1600/DSC00545_brvv4t`;
const HERO_IMAGE_SRC_SET = [768, 1280, 1600, 1920]
  .map((width) => `${HERO_IMAGE_BASE},w_${width}/DSC00545_brvv4t ${width}w`)
  .join(", ");

const CTA_IMAGE_SRC = `${CTA_IMAGE_BASE},w_768/DSC01487_sjedae`;
const CTA_IMAGE_SRC_SET = [480, 768, 960]
  .map((width) => `${CTA_IMAGE_BASE},w_${width}/DSC01487_sjedae ${width}w`)
  .join(", ");

export default function Index() {
  const { t } = useTranslation();

  const services = [
    t("home.services.portraits", { returnObjects: true }),
    t("home.services.couples", { returnObjects: true }),
    t("home.services.families", { returnObjects: true }),
    t("home.services.events", { returnObjects: true }),
  ] as { title: string; desc: string; price: string }[];

  return (
    <main id="main-content">
      <SEOHead
        title="Professional Photographer in Szeged, Hungary"
        description="Professional outdoor portrait, couples, family and event photographer in Szeged, Hungary. Natural light photography by Tamás. Book your session today."
        titleHu="Fotós Szegeden | Portré, Páros, Családi Fotózás"
        descriptionHu="Profi szabadtéri portré-, páros-, családi és eseményfotózás Szegeden. Természetes fényű fotózás Tamástól. Foglalj időpontot még ma!"
        canonical="https://capshotvisuals.com/"
      />
      {/* HERO — full-bleed image with overlay */}
      <section className="relative min-h-[85vh] md:min-h-[130vh] lg:min-h-[140vh] flex items-center justify-center overflow-hidden pt-[20vh] md:pt-[28vh] lg:pt-[32vh]">
        <div className="absolute inset-0">
          <img
            src={HERO_IMAGE_SRC}
            srcSet={HERO_IMAGE_SRC_SET}
            sizes="100vw"
            alt="Outdoor portrait photography by Capshot Visuals in Szeged, Hungary — natural light session"
            width={1920}
            height={1893}
            fetchPriority="high"
            loading="eager"
            decoding="async"
            className="w-full h-full object-cover object-[center_40%]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(0_0%_0%/0)] via-[hsl(0_0%_0%/0.2)] to-[hsl(0_0%_0%/0.25)]" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl">
          <h1
            className="slide-in-up font-display text-5xl md:text-7xl lg:text-8xl leading-[1.0] tracking-tight text-[hsl(0_0%_100%/1)] mb-6"
            style={{ animationDelay: "0.5s" }}
          >
            {t("home.heroTitle").split("\n")[0]}
            <br />
            <span className="font-display italic font-normal">{t("home.heroTitle").split("\n")[1]}</span>
          </h1>

          <div
            className="hero-line-reveal w-24 h-px bg-[hsl(0_0%_100%/0.4)] mx-auto my-8"
            style={{ animationDelay: "1s" }}
          />

          <p
            className="animate-fade-in text-xs md:text-sm text-[hsl(0_0%_100%/1)] tracking-[0.25em] uppercase mb-12"
            style={{ animationDelay: "1.2s" }}
          >
            {t("home.heroSubtitle")}
          </p>

          <div className="slide-in-up" style={{ animationDelay: "1.5s" }}>
            <Link
              to="/contact"
              className="inline-flex items-center gap-3 px-10 py-4 border border-[hsl(0_0%_100%/0.3)] text-[hsl(0_0%_100%/0.75)] text-[10px] font-medium tracking-[0.3em] uppercase hover:bg-[hsl(0_0%_100%/0.9)] hover:text-[hsl(0_0%_0%)] transition-all duration-500"
            >
              {t("home.bookSession")} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="px-6 py-16 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mb-10 md:mb-20 text-center"
          >
            <p className="text-[10px] tracking-[0.35em] uppercase text-warm-gold font-medium mb-5">{t("home.sessionsLabel")}</p>
            <h2 className="font-display text-4xl md:text-6xl text-foreground">{t("home.whatIOffer")}</h2>
            <div className="album-divider w-20 mx-auto mt-8" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-border">
            {services.map((service, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08, duration: 0.6 }}
                className="bg-background p-6 md:p-10 flex flex-col group"
              >
                <p className="text-[10px] tracking-[0.25em] uppercase text-warm-gold font-medium mb-4">0{i + 1}</p>
                <h3 className="font-display text-2xl mb-4 text-foreground group-hover:text-warm-gold transition-colors duration-300">{service.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed mb-6 flex-1 font-light">{service.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-14 text-center">
            <Link
              to="/services"
              className="inline-flex items-center gap-3 px-10 py-4 border border-border text-foreground text-[10px] font-medium tracking-[0.25em] uppercase hover:border-foreground transition-colors duration-300"
            >
              {t("home.viewAllPackages")} <ArrowRight size={12} />
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <TestimonialCarousel testimonials={t("home.testimonials", { returnObjects: true }) as { quote: string; author: string; session: string }[]} />

      {/* CTA */}
      <section className="bg-background px-6 py-14 md:py-28">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div className="flex items-center justify-center">
            <img
              src={CTA_IMAGE_SRC}
              srcSet={CTA_IMAGE_SRC_SET}
              sizes="(min-width: 1024px) 35vw, 75vw"
              alt="Couples photography session at golden hour in Szeged by Capshot Visuals"
              width={1365}
              height={2048}
              className="w-3/4 object-contain"
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="flex items-center justify-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center max-w-md"
            >
              <div className="w-16 h-px bg-foreground/20 mx-auto mb-5 md:mb-10" />
              <h2 className="font-display text-4xl md:text-5xl mb-4 md:mb-6 text-foreground">{t("home.readyTitle")}</h2>
              <p className="text-xs text-muted-foreground tracking-[0.2em] mb-6 md:mb-12 uppercase font-light">{t("home.readySubtitle")}</p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-3 px-10 py-4 border border-foreground/30 text-foreground text-[10px] font-medium tracking-[0.25em] uppercase hover:bg-foreground hover:text-primary-foreground transition-all duration-500"
              >
                {t("home.getInTouch")} <ArrowRight size={12} />
              </Link>
              <div className="w-16 h-px bg-foreground/20 mx-auto mt-5 md:mt-10" />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}

